
import styled, { useTheme } from "styled-components"
import { BottomPanel, BottomPanelState, Button, ExploreMenu as ExploreMenuComponent, Fader, FadeRoot, Icon, PageRoot, RoundButton, SmoothFade, Tab, PrimaryCtaButton } from "slate-ui-components"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Visualizer, Is3DAllowed } from "./components/Visualizer"
import { VisualizerView } from "./components/VisualizerView"
import { Measured } from "./Measured"
import { useAppDispatch, useAppSelector } from "./store/hooks"
import { Summary } from "./SummaryTab"
import { LoadingOverlay } from "./LoadingOverlay"
import { IdleOverlay } from "./IdleOverlay"
import { ConfigurationTab } from "./ConfigurationTab"
import { ExploreOptions, ExploreOptionsList } from "./Explore"

import { Notifications } from './Notifications';

import { ShareButton as _ShareButton } from "slate-ui-components"
import {  useIntl } from "react-intl"
import { useShareDialog } from "./Dialogs/ShareDialog"
import { useModeCallOutDialog } from "./Dialogs/ModeCallOut"
import { loadSavedBuild, VisualizerSlice } from "./store/reducers/visualizer"
import { useDebounce } from "react-use"
import { BuildData, configurationChange, registerLoadBuildHandler, saveBuild, complete, JSONValue } from "@zerolight-core/iframe-communications"
import { getBuildData } from "./helpers"
import { useBuildSession } from "./BuildSession"
import { Action, IBuildSession } from "@zerolight-core/generic-buildsession"
import { useDirect2D } from "./components/Direct2D"
import { HotspotManager } from "@zerolight-core/libzl-react"
import { Hotspot } from "./components/Hotspot"
import { ZLVEVisualizerState } from "./types/VisualizerTypes"
import { JSONEquality } from "./utils/equal"
import { AppConfig, PreloadImageLandscape, PreloadImagePortrait } from "./config"
import { useMediaQuery } from "@react-hook/media-query"
import { useAnalytics } from "./AnalyticsProvider"
import { useFrameState } from "./FrameStateProvider"
import { getInitialUrlArgs } from "./UrlArgsProvider"
import { AnimationStates } from "@zerolight-core/libzl"

const Container = styled.div`
	position: relative;
	height: 100%;
	width: 100%;
	display: flex;
	flex-flow: column nowrap;
	overflow: hidden;
	max-width: 1286px;
	margin: auto;
`

const Viewport = styled.div`
	flex-basis: 100%;
	flex-shrink: 1;
	width: 100%;
	position: relative;
	overflow: hidden;
    background-color: #262626;
	background-image: url(${PreloadImageLandscape});
	@media (orientation:portrait) {
		background-image: url(${PreloadImagePortrait});
	}
	background-position: center center;
	background-size: cover;

	&:first-child::before{
		content: "";
		background-color: rgba(128, 128, 128, 0.5);
		position: absolute;
		inset: 0;
	}	
`

const ExploreMenu = styled(ExploreMenuComponent)`
	@media ${p => p.theme.breakpoints.fullScreenStream}{
		padding: 16px 18px;
	}
`

const Overlays = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
`

const DesktopModes = styled.div`
	display: none;
	@media screen and (pointer: fine) {
		position: absolute;
		top: 20px;
		width: 100%;
		display: flex;
		justify-content: center;
	}
`
const DesktopModesPanel = styled.div`
	display: flex;
	justify-content: center;
	flex-flow: row nowrap;
	border-radius: 6px;
	pointer-events: auto;
	background-color: ${({theme}) => theme.colours.background.normal};
	z-index: 1;

	&>button {
		min-width: 141px;
		min-height: 39px;
	}
`


const FullscreenShareButton = styled(_ShareButton)`
	position: absolute;
	bottom: 120px;
	right: 20px;
	pointer-events: auto;
	z-index: 10;
`

const ExitFullScreenButton = styled(RoundButton)`
	position: absolute;
	bottom: 170px;
	right: 20px;
	pointer-events: auto;
	z-index: 10;
`

const SaveButton = styled(RoundButton)`
	position: absolute;
	bottom: 70px;
	right: 20px;
	pointer-events: auto;
	z-index: 10;
`

const EnvironmentButton = styled(RoundButton)`
	position: absolute;
	bottom: 70px;
	left: 20px;
	pointer-events: auto;
	z-index: 10;
`


const FullScreenReserveButton = styled(PrimaryCtaButton)`
	position: absolute;
	top: 20px;
	right: 20px;
	pointer-events: auto;
	z-index: 1;
`


const VisualizerGap = styled.div`
	position: "relative";
	transition: flex-basis 0.3s linear;
	background-color: #262626;
	box-sizing: border-box;
	flex-shrink: 0;
	flex-grow: 0;
	flex-basis: 100%;
`

const VisualiserScaling = styled(Measured)`
	position: relative;
	transition: height 0.3s linear, transform 0.3s linear;
	transform-origin: 50% 0%;
`



export const Configurator = () =>
{
	const { current: build, masterModel, lastChange } = useBuildSession()
	const d2d = useDirect2D()
	const theme = useTheme();
	const isFullScreenLayout = useMediaQuery(theme.breakpoints.fullScreenStream);
	const {min3DConfigChanges} = getInitialUrlArgs()

	const dispatch = useAppDispatch();
	const intl = useIntl();
	const share = useShareDialog("Viewport")
	const modeCallOut = useModeCallOutDialog()
	const modes = useAppSelector(s => s.configuration.currentVisualizerConfig.allowedModes);
	const modesReady = useAppSelector(s => s.configuration.modesReady)
	const hotspots = useAppSelector(s => s.configuration.selectedOptions.hotspots)
	const { environment, prcodes, preview } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, JSONEquality);
	const { customWraps, appliedWraps } = useAppSelector(s => s.configuration.currentVisualizerConfig.configuration);
	const currentMode = useAppSelector(s => s.configuration.currentMode);
	const requestedMode = useAppSelector(s => s.configuration.requestedMode);
	const busy = useAppSelector(s => !s.configuration.modesReady[s.configuration.currentMode] || s.configuration.modesBusy[s.configuration.currentMode]);

	const [shownModeCallout,setShownModeCallout] = useState(false);
	const [finishedShowModeTimeOut,setFinishedShowModeTimeOut] = useState(false);
	const [startedShowModeTimeOut,setStartedShowModeTimeOut] = useState(false);

	const [state, setState] = useState<BottomPanelState>("collapsed");
	const [normalStateHeight, setNormalHeight] = useState(0);
	const [summaryStateHeight, setSummaryHeight] = useState(0);
	const [pendingSummaryHeight, setPendingSummaryHeight] = useState(0)
	const [exploreMenuHeight, setExploreMenuHeight] = useState(0);
	const { isFullscreen, fullscreenPreferred, exitFullScreen, enterFullScreen } = useFrameState();
	const getGapHeight = () => state == "collapsed" || isFullScreenLayout ? 0 : (normalStateHeight - exploreMenuHeight + 8);
	const [gapHeight, setGapHeight] = useState(getGapHeight);
	const [visualizerHeight, setVisualizerHeight] = useState(0);

	const configurationChanges = useAppSelector((s) => s.configuration.changes)

	const environmentAnimations = AppConfig.visualizer.configuration.optionsGroups.environments.options[environment]?.visualizerState?.forceAnimationStates as (AnimationStates | undefined)

	const reportedConfigurationChange = useRef<IBuildSession|undefined>(build)
	
	useDebounce(() => setGapHeight(getGapHeight()), 100, [normalStateHeight, state, exploreMenuHeight]);
	const {record} = useAnalytics()

	useEffect(() => {
		record?.(["Configurator", "MenuMode"], {mode: {expanded: "summary", collapsed: "explore", normal: "configure"}[state] })

		if(state !== "collapsed") {
			dispatch(VisualizerSlice.actions.setSceneOptionGroup())	
		}
	}, [record, state, dispatch])	


	useEffect(() => {
		if(dispatch) {
			const timer = setTimeout(() => {
				dispatch(VisualizerSlice.actions.setActiveCategory("body_style"))
				setState("normal")
			}, 500); // 500ms delay
		
			return () => clearTimeout(timer); // Cleanup on unmount
		}
		return
	}, [dispatch]);

	console.log("3D Upgrade Current Mode", currentMode)
	useEffect(() => {
		if(currentMode === "2D") {
			dispatch(VisualizerSlice.actions.setChanges(0))
			dispatch(VisualizerSlice.actions.setPendingColourPicker(false))
		}
		if(currentMode === "3D") {
			dispatch(VisualizerSlice.actions.setPendingColourPicker(false))
		}
	}, [currentMode, dispatch])

	const submitChangeEvent =  useCallback(async () => {
		if(build) {
			const buildData = await getBuildData(intl, masterModel!, prcodes, environmentAnimations, environment, build, customWraps, appliedWraps, d2d, false)
			let action : "add" | "remove" | "reset" = "reset"
			switch(lastChange?.action) {
				case Action.AddedAccessory:
				case Action.ChangedFeature:
				case Action.ReplacedAccessory:
					action = "add";
					break
				case Action.RemovedAccessory:
					action = "remove"
					break
			}
			configurationChange(
				buildData,
				action,
				lastChange?.item?.id,
				lastChange?.resolution?.add?.map(i => i.id),
				lastChange?.resolution?.remove?.map(i => i.id)
			)

			if(lastChange?.item) {
				const duplicate = reportedConfigurationChange.current === build
				reportedConfigurationChange.current = build
				record?.(["Configurator", "ConfigurationChanged"], {action, item: lastChange.item?.id, replaced: lastChange.replaced?.id, added: lastChange.resolution?.add.map(i => i.id), removed: lastChange.resolution?.remove.map(i => i.id), duplicate})				
				dispatch(VisualizerSlice.actions.incrementChanges())
			}
		}
	}, [build, intl, masterModel, prcodes, environmentAnimations, environment, customWraps, appliedWraps, d2d, lastChange?.action, lastChange?.item, lastChange?.resolution?.add, lastChange?.resolution?.remove, lastChange?.replaced?.id, record, dispatch])

	useEffect(() => {
		const callback = (_: string, data: BuildData) => {
			dispatch(loadSavedBuild(data))
		}
		return registerLoadBuildHandler(callback)
	}, [dispatch])

	useEffect(() => {
		submitChangeEvent()
	}, [prcodes, masterModel, environment])

	useEffect(() => {
		if(state !== "expanded" && pendingSummaryHeight > 0) {
			setSummaryHeight(pendingSummaryHeight)
		}
	}, [pendingSummaryHeight, state])

	const allowed3D = Is3DAllowed(modes, configurationChanges)
	const allowedModes = useMemo(() => {
		return allowed3D ? modes : modes.filter(mode => mode !== "3D")
	}, [modes, allowed3D])

	useEffect(() =>
	{		
		if (modesReady["2D"] && !modesReady["3D"] && !startedShowModeTimeOut){

			if(allowed3D){
				setTimeout(() => {
					setFinishedShowModeTimeOut(true)					
				}, 1500); 
				setStartedShowModeTimeOut(true);				
			} 
		}		

		if(finishedShowModeTimeOut && !shownModeCallout){

			if (!modesReady["3D"]){
				//modeCallOut.open();
			}
			setShownModeCallout(true);
		}

	},[modesReady, shownModeCallout, finishedShowModeTimeOut, startedShowModeTimeOut, allowed3D]);

	const exploreLabel = intl.formatMessage({id: "mode.explore", defaultMessage: "Explore"})
	const personaliseLabel = intl.formatMessage({id: "mode.personalise", defaultMessage: "Personalize"})
	const summaryLabel = intl.formatMessage({id: "mode.summary", defaultMessage: "Summary"})

	const showHotspots = hotspots.includes("on") && currentMode === "3D" && requestedMode === "3D"
	const modeSpecificChildren = useMemo(() => {
		if(!showHotspots) {
			return undefined;
		}
		return {
			"2D": null,
			"3D": <HotspotManager template={Hotspot}/>
		}
	}, [showHotspots])

	const saved = useAppSelector(s => s.configuration.saved)
	const [saving, setSaving] = useState(false)
	const doSave = useCallback(async () => {
		if(masterModel && build) {
			setSaving(true)
			const buildData = await getBuildData(intl, masterModel, prcodes, environmentAnimations, environment, build, customWraps, appliedWraps, d2d, true)
			try {
				await saveBuild(buildData)
				dispatch(VisualizerSlice.actions.setSaved(true))
				record?.(["Configurator", "SaveBuild"], {prcodes, configuration: build.getSelectedItems(true).map(i => i.id)})
			} catch (error: unknown) {
				console.error("An error occurred while saving", error)
				dispatch(VisualizerSlice.actions.setSaved(false))
			}
			setSaving(false)
		}
	}, [appliedWraps, build, customWraps, d2d, dispatch, environment, environmentAnimations, intl, masterModel, prcodes, record])
	
	const reserveLabel = intl.formatMessage({id: "summary.reserve", defaultMessage: "RESERVE A BLANK SLATE"})

	const onReserve = async () => {
		if(build) {
				const buildData = await getBuildData(intl, masterModel!, prcodes, environmentAnimations, environment, build, customWraps, appliedWraps, d2d, true)

				record?.(["Configurator", "Reserve"], {prcodes, configuration: build.getSelectedItems(true).map(i => i.id), customWrap: Object.values(customWraps).find(w => w.selected) as unknown as JSONValue})
				complete(buildData)
		}			
	}

	
	const displayStreamOverlayActions = Object.values(modesReady).some(v => v) && !preview?.length;

	const exploreEnvironments = useCallback(() => {
		setState("collapsed")
		dispatch(VisualizerSlice.actions.setSceneOptionGroup("environments"))	
		record?.(["Configurator", "Environment"])
	}, [dispatch, record])

	const drag = use2DGalleryDrag();
	return (
		<PageRoot>
			<Container onPointerDownCapture={() => dispatch(VisualizerSlice.actions.interactedWithStream())}>
				<Viewport id="viewport" tabIndex={0} {...drag}>
						<Visualizer modes={allowedModes} modeSpecificChildren={modeSpecificChildren}>
							<VisualiserScaling updateHeight={setVisualizerHeight} style={{ height: `calc(100% + ${gapHeight}px)`, width: "130%", transform: `translateX(-${15/130*100}%) scale(${1 - gapHeight / visualizerHeight})` }}>
								<VisualizerView/>
							</VisualiserScaling>
						</Visualizer>
					<Overlays>
						{!preview?.length &&
							<DesktopModes>
								<DesktopModesPanel>
									<Button label={exploreLabel} onClick={() => setState("collapsed")}><Tab selected={state === "collapsed"} label={exploreLabel}/></Button>
									<Button label={personaliseLabel} onClick={() => setState("normal")}><Tab selected={state === "normal"} label={personaliseLabel}/></Button>
									<Button label={summaryLabel} onClick={() => setState("expanded")}><Tab selected={state === "expanded"} label={summaryLabel}/></Button>
								</DesktopModesPanel>
							</DesktopModes>
						}
						<LoadingOverlay busy={busy}/>
						<IdleOverlay />					
					
						{!isFullScreenLayout && displayStreamOverlayActions && state !== "collapsed" &&
							<EnvironmentButton label={intl.formatMessage({ id: "sceneCategory.environments", defaultMessage: "Environments" })} onClick={exploreEnvironments} testId="environments">
								<Icon icon="Backgrounds"/>
							</EnvironmentButton>
						}

						{isFullscreen && displayStreamOverlayActions &&
							<FullScreenReserveButton testId="fullscreen.reserve" label={reserveLabel} onClick={onReserve}>
								{reserveLabel}								
							</FullScreenReserveButton>
						}

						{displayStreamOverlayActions &&
							<SaveButton label={intl.formatMessage({id: "share.save_build", defaultMessage: "SAVE"})} onClick={doSave} testId="save" disabled={saving || saved}>
								<Icon icon={saved?"HeartFilled":"Heart"} viewBox="0 0 14 14"/>
							</SaveButton>
						}

						{displayStreamOverlayActions &&
							<FullscreenShareButton label={intl.formatMessage({id: "general.share", defaultMessage: "Share"})} testId="share" onClick={share.open}/>
						}

						{isFullscreen && displayStreamOverlayActions &&
							<ExitFullScreenButton label={intl.formatMessage({id: "exit-fullscreen", defaultMessage: "Exit Fullscreen"})} testId="exit-fullscreen" onClick={exitFullScreen}>
								<Icon icon="ExitFullscreen"/>
							</ExitFullScreenButton>
						}

						{!isFullscreen && fullscreenPreferred && displayStreamOverlayActions &&
							<ExitFullScreenButton label={intl.formatMessage({id: "fullscreen", defaultMessage: "Full Screen"})} testId="enter-fullscreen" onClick={enterFullScreen}>
								<Icon icon="Fullscreen"/>
							</ExitFullScreenButton>
						}						
						
					</Overlays>
					<ExploreOptionsList visible={state == "collapsed" || isFullScreenLayout} />					
					<Notifications panelState={ state } />
				</Viewport>
				<Measured updateHeight={setExploreMenuHeight}>
					{!isFullScreenLayout && <ExploreMenu>
						<ExploreOptions />
					</ExploreMenu>}
				</Measured>
				<VisualizerGap style={{height: gapHeight + "px", flexBasis: gapHeight + "px"}}/>
				<Fader show={state==="expanded"} onClick={() => setState("normal")}/>
				{!isFullScreenLayout && <BottomPanel state={state} onStateChange={setState} normalHeight={normalStateHeight + 18} maxHeight={summaryStateHeight + 18}>
					<FadeRoot>
						<SmoothFade value={state != "expanded" ? 1 : 0} direction="down">
							<Measured updateHeight={setNormalHeight}>
								<ConfigurationTab configuratorStateFunc={setState} panelState={state} onCollapse={() => setState("collapsed")} onExpand={() => setState("normal")} />
							</Measured>
						</SmoothFade>
						<SmoothFade value={state == "expanded" ? 1 : 0} direction="up">
							{state === "expanded" ?
								<Summary />
								:
								<Measured updateHeight={setPendingSummaryHeight}>
									<Summary />
								</Measured>
							}
						</SmoothFade>
					</FadeRoot>
				</BottomPanel>}
				{share.element}
				{modeCallOut.element}
			</Container>
		</PageRoot>
	)
}



function use2DGalleryDrag()
{
	const currentMode = useAppSelector(s => s.configuration.currentMode);
	const dispatch = useAppDispatch();
	const threshold = 100;
	return useMemo(() =>
	{
		let start = [0,0];
		function onMove(e: PointerEvent)
		{
			const delta = [e.clientX - start[0], e.clientY - start[1]];

			if (delta[0] > threshold)
			{
				start[0] += threshold;
				dispatch(VisualizerSlice.actions.nextCamera(-1));
			} 
			if (delta[0] < -threshold)
			{
				start[0] -= threshold;
				dispatch(VisualizerSlice.actions.nextCamera(1));
			} 
		}

		return {
			onPointerDownCapture: (e: React.PointerEvent<HTMLDivElement>) =>
			{
				if (currentMode != "2D")
					return;

				e.stopPropagation();
				e.preventDefault();
				const currentTarget = e.currentTarget;
				start = [e.clientX, e.clientY];

				currentTarget.addEventListener("pointermove", onMove, { capture: true});

				function unsubscribe()
				{
					currentTarget.removeEventListener("pointerup", unsubscribe, { capture: true});
					currentTarget.removeEventListener("pointercancel", unsubscribe, { capture: true});
					currentTarget.removeEventListener("pointermove", onMove, { capture: true});
				}
				currentTarget.addEventListener("pointerup", unsubscribe, { capture: true});
				currentTarget.addEventListener("pointercancel", unsubscribe, { capture: true});
			}
		}
	}, [currentMode, dispatch]);
}

